import { useEffect, useMemo } from 'react';
import { throttle } from 'lodash';
import { NetworkStatus } from '@apollo/client';
import {
  GetOpeningRecommendedUserCardsQuery,
  GetOpeningRecommendedUserCardsQueryVariables,
  SignalEntityTypeEnum,
  SignalTypeEnum,
  useGetOpeningRecommendedUserCardsLazyQuery,
  useSessionQuery,
} from '@src/graphql/generated';
import { useGetOpeningId } from '@src/views/Opening/hooks/useGetOpeningId';
import { DEFAULT_CURRENCY_CODE } from '@src/constants';
import { handleFetchMore } from '@src/util/handleFetchMore';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { recordSignals } from '@src/graphql/signals/actions';
import { QueryParams } from '../../../interfaces/queryParams.interface';
import {
  UseGetOpeningRecommendedUserCardsQueryParams,
  UseGetOpeningRecommendedUserCardsQueryValue,
} from './interfaces/useGetOpeningRecommendedUsers.interface';

const FIRST_BATCH_SIZE = 9;
const NEXT_BATCH_SIZE = 6;

export const useGetOpeningRecommendedUsers = ({
  excludedUserIds,
}: UseGetOpeningRecommendedUserCardsQueryParams): UseGetOpeningRecommendedUserCardsQueryValue => {
  const openingId = useGetOpeningId();

  const { queryParams } = useQueryParams<QueryParams>();

  const { data: sessionData } = useSessionQuery();

  const recruiter = sessionData?.currentRecruiter;

  const [
    fetchOpeningRecommendedUsers,
    {
      data: openingRecommendedUsersData,
      loading: isOpeningRecommendedUsersLoading,
      fetchMore: fetchMoreOpeningRecommendedUsers,
      refetch: refetchOpeningRecommendedUsers,
      updateQuery: updateOpeningRecommendedUsersQuery,
      networkStatus: openingRecommendedUsersNetworkStatus,
    },
  ] = useGetOpeningRecommendedUserCardsLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const recommendedUsers = openingRecommendedUsersData?.opening?.recommendedUsers.result || [];
  const recommendedUsersPagination = openingRecommendedUsersData?.opening?.recommendedUsers.pagination;

  const isOpeningRecommendedUsersInitialLoading = [NetworkStatus.loading].includes(
    openingRecommendedUsersNetworkStatus,
  );

  const openingRecommendedUsersVariables = useMemo<GetOpeningRecommendedUserCardsQueryVariables | undefined>(() => {
    if (!openingId) {
      return undefined;
    }

    return {
      openingId: openingId,
      paginationOptions: {
        limit: FIRST_BATCH_SIZE,
        sortBy: queryParams.recommendedUsersSortOption,
      },
      recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
    };
  }, [openingId, recruiter?.country?.currency, queryParams.recommendedUsersSortOption]);

  const loadMoreRecommendedUsers = throttle(() => {
    if (!isOpeningRecommendedUsersLoading && recommendedUsersPagination?.hasMoreResults) {
      handleFetchMore(fetchMoreOpeningRecommendedUsers, {
        updateQuery: (
          previousResult: GetOpeningRecommendedUserCardsQuery,
          { fetchMoreResult }: { fetchMoreResult: GetOpeningRecommendedUserCardsQuery },
        ): GetOpeningRecommendedUserCardsQuery => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          const previousApplicants = previousResult.opening?.recommendedUsers;

          const newResult = fetchMoreResult.opening?.recommendedUsers.result;
          const pagination = fetchMoreResult.opening?.recommendedUsers.pagination;

          recordSignals({
            currentLimit: previousApplicants.pagination.limit || 0,
            items: previousApplicants.result.map(item => ({
              id: item.userVerification.id,
              type: SignalEntityTypeEnum.USER_VERIFICATION,
            })),
            signalType: SignalTypeEnum.VIEW_USER_VERIFICATION_LIST_ITEM,
          });

          return {
            ...previousResult,
            opening: {
              ...previousResult.opening,
              recommendedUsers: {
                ...previousResult.opening.recommendedUsers,
                pagination: {
                  ...previousApplicants.pagination,
                  hasMoreResults: pagination.hasMoreResults,
                  limit: pagination.limit,
                  maxCursor: pagination.maxCursor,
                },
                result: [...previousApplicants.result, ...newResult],
              },
            },
          };
        },
        variables: {
          ...openingRecommendedUsersVariables,
          excludedUserIds: excludedUserIds,
          paginationOptions: {
            ...openingRecommendedUsersVariables?.paginationOptions,
            limit: NEXT_BATCH_SIZE,
            minCursor: recommendedUsersPagination.maxCursor,
          },
        },
      });
    }
  }, 200);

  const removeVerifiedUser = (userId: string) => {
    const userIndex = recommendedUsers.findIndex(recommendedUser => recommendedUser.user.id === userId);

    updateOpeningRecommendedUsersQuery(data => {
      return {
        ...data,
        opening: {
          ...data.opening,
          recommendedUsers: {
            ...data.opening.recommendedUsers,
            result: data.opening.recommendedUsers.result.filter(item => item.user.id !== userId),
          },
        },
      };
    });

    if (recommendedUsers.length - userIndex < 5) {
      loadMoreRecommendedUsers();
    }
  };

  useEffect(() => {
    if (!openingRecommendedUsersVariables) {
      return;
    }

    if (!openingRecommendedUsersData) {
      fetchOpeningRecommendedUsers({
        variables: openingRecommendedUsersVariables,
      });

      return;
    }

    refetchOpeningRecommendedUsers(openingRecommendedUsersVariables);
  }, [openingRecommendedUsersVariables]);

  return {
    isOpeningRecommendedUsersInitialLoading: isOpeningRecommendedUsersInitialLoading,
    isOpeningRecommendedUsersLoading: isOpeningRecommendedUsersLoading,
    loadMoreRecommendedUsers: loadMoreRecommendedUsers,
    openingState: openingRecommendedUsersData?.opening.state,
    recommendedUsers: recommendedUsers,
    recommendedUsersPagination: recommendedUsersPagination,
    removeVerifiedUser: removeVerifiedUser,
  };
};
